import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Trophy, BookOpen, ListChecks, Mic, Pencil, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import ConceptTeachCard from '@/components/ConceptTeachCard';
import PracticeStep from '@/components/lessons/PracticeStep';
import SayItStep from '@/components/lessons/SayItStep';
import TraceWordStep from '@/components/lessons/TraceWordStep';

const STEP_META = {
  teach: { label: 'Learn', icon: BookOpen },
  practice: { label: 'Practice', icon: ListChecks },
  say: { label: 'Say it', icon: Mic },
  trace: { label: 'Trace', icon: Pencil },
};

const StepDots = ({ steps, current }) => (
  <div className="flex items-center justify-center gap-2 mb-6" data-testid="lesson-steps">
    {steps.map((s, i) => {
      const Icon = STEP_META[s].icon;
      const state = i < current ? 'bg-teal-500 text-white' : i === current ? 'bg-amber-400 text-white scale-110' : 'bg-slate-200 text-slate-500';
      return (
        <div key={s} className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-transform ${state}`}>
          <Icon className="w-3.5 h-3.5" /> {STEP_META[s].label}
        </div>
      );
    })}
  </div>
);

const LibraryLessonPlayer = () => {
  const { childId, lessonId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [score, setScore] = useState(0);

  useEffect(() => {
    api.get(`/lessons/item/${lessonId}/play`, { params: { child_id: childId } })
      .then((r) => setLesson(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not open this lesson'));
  }, [lessonId]); // eslint-disable-line react-hooks/exhaustive-deps

  const steps = useMemo(() => {
    if (!lesson) return [];
    return ['teach', lesson.questions?.length && 'practice', lesson.say_words?.length && 'say', lesson.trace_words?.length && 'trace'].filter(Boolean);
  }, [lesson]);

  const teachData = useMemo(() => lesson && ({
    title: lesson.title, illustration: lesson.illustration, source: 'ai', ...lesson.teach,
  }), [lesson]);

  const advance = (points = 0) => {
    const total = score + points;
    setScore(total);
    if (stepIdx + 1 < steps.length) { setStepIdx((i) => i + 1); return; }
    setStepIdx(steps.length);
    if (childId) {
      api.post(`/lessons/item/${lessonId}/complete`, {
        child_id: childId, score: total, total: (lesson.questions?.length || 0) + (lesson.say_words?.length || 0),
      }).catch(() => {});
    }
  };

  const back = () => navigate(childId ? `/learn/${childId}` : '/lessons/library');
  if (!lesson) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>;
  }
  const step = steps[stepIdx];

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/60 via-white to-amber-50/40 pb-24" data-testid="library-lesson-player">
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" size="icon" onClick={back} data-testid="lesson-player-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="font-fredoka text-xl font-bold text-slate-800 truncate" data-testid="lesson-player-title">{lesson.title}</h1>
          {!childId && <span className="ml-auto text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold">Preview</span>}
        </div>
        {step && <StepDots steps={steps} current={stepIdx} />}
        {step === 'teach' && <ConceptTeachCard lessonData={teachData} onDone={() => advance(0)} />}
        {step === 'practice' && <PracticeStep questions={lesson.questions} onDone={advance} />}
        {step === 'say' && <SayItStep words={lesson.say_words} onDone={advance} />}
        {step === 'trace' && <TraceWordStep words={lesson.trace_words} onDone={() => advance(0)} />}
        {!step && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10" data-testid="lesson-player-done">
            <div className="mx-auto w-24 h-24 rounded-full bg-teal-100 flex items-center justify-center mb-4"><Trophy className="w-12 h-12 text-teal-600" /></div>
            <h2 className="font-fredoka text-3xl font-bold text-slate-900 mb-2">Lesson complete!</h2>
            <p className="text-slate-600 mb-6">You earned <strong data-testid="lesson-player-score">{score}</strong> stars.</p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={() => { setStepIdx(0); setScore(0); }} data-testid="lesson-player-again-btn">Do it again</Button>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={back} data-testid="lesson-player-finish-btn">All done</Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default LibraryLessonPlayer;
